import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Menu, 
  X, 
  Headphones, 
  Newspaper, 
  Mail, 
  Mic, 
  Info, 
  Users, 
  ChevronDown,
  Radio,
  Lightbulb,
  Megaphone,
  Music,
  BookOpen,
  Trophy,
  ListMusic,
  LayoutDashboard,
  GraduationCap,
  Building2,
  School,
  BookOpen as BookOpenIcon,
  Briefcase
} from 'lucide-react';
import AuthStatus from '../AuthStatus';
import { createPortal } from 'react';

const navItems = [
  {
    id: 'project',
    label: 'Projeto Sinopse',
    icon: Info,
    submenu: [
      { path: '/project', name: 'Sobre o Projeto', icon: Info },
      { path: '/team', name: 'A Nossa Equipa', icon: Users }
    ]
  },
  {
    id: 'agrupamento',
    label: 'Agrupamento AEAAV',
    icon: School,
    submenu: [
      {
        id: 'cientifico',
        name: 'Cursos Científico-Humanísticos',
        icon: BookOpenIcon,
        path: '/agrupamento/secundario/cientifico'
      },
      { 
        id: 'profissional',
        name: 'Cursos Técnico Profissionais',
        icon: Briefcase,
        path: '/courses'
      }
    ]
  },
  {
    id: 'podcasts',
    label: 'Podcasts',
    icon: Headphones,
    submenu: [
      { path: '/podcasts/all', name: 'Ver Todos', icon: ListMusic },
      { path: '/podcasts/latest', name: 'Últimos Episódios', icon: Radio },
      { path: '/podcasts/projects', name: 'Projetos Escolares', icon: Lightbulb },
      { path: '/podcasts/news', name: 'Notícias e Atualidades', icon: Megaphone },
      { path: '/podcasts/culture', name: 'Arte e Cultura', icon: Music },
      { path: '/podcasts/education', name: 'Educação', icon: BookOpen },
      { path: '/podcasts/sports', name: 'Desporto Escolar', icon: Trophy }
    ]
  },
  { id: 'news', path: '/news', name: 'Notícias', icon: Newspaper },
  { id: 'contact', path: '/contact', name: 'Contacto', icon: Mail },
  { id: 'cms', path: '/cms', name: 'CMS', icon: LayoutDashboard }
];

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [openMenus, setOpenMenus] = useState<string[]>([]);
  const [selectedMenu, setSelectedMenu] = useState<string | null>(null);
  const location = useLocation();
  const [submenuHeight, setSubmenuHeight] = useState<{[key: string]: number}>({});
  const submenuRefs = useRef<{[key: string]: HTMLDivElement | null}>({});
  const [hoveredMenu, setHoveredMenu] = useState<string | null>(null);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsOpen(false);
    setOpenMenus([]);
    setSelectedMenu(null);
    setHoveredMenu(null);
  }, [location]);

  const toggleMenu = (menuId: string, e?: React.MouseEvent) => {
    e?.stopPropagation();
    if (selectedMenu === menuId) {
      setSelectedMenu(null);
      setOpenMenus(prev => prev.filter(id => id !== menuId));
    } else {
      setSelectedMenu(menuId);
      setOpenMenus([menuId]);
    }
  };

  const isMenuOpen = (menuId: string) => selectedMenu === menuId || openMenus.includes(menuId);

  const renderSubmenu = (items: any[], level = 0) => {
    return items.map((item) => {
      if (item.submenu) {
        return (
          <div key={item.id} className="mb-2 relative">
            <button
              onClick={(e) => toggleMenu(item.id, e)}
              className={`w-full flex items-center justify-between px-4 py-3 rounded-lg text-foreground/80 bg-secondary/50 hover:bg-secondary transition-colors z-1`}
              onMouseDown={(e) => e.stopPropagation()}
            >
              <div className="flex items-center gap-3">
                <item.icon className="w-5 h-5" />
                <span className="font-medium">{item.name || item.label}</span>
              </div>
              <ChevronDown 
                className={`w-5 h-5 transition-transform duration-200 ${
                  isMenuOpen(item.id) ? 'rotate-180' : ''
                }`} 
              />
            </button>
            <AnimatePresence initial={false}>
              {isMenuOpen(item.id) && (
                <div
                  ref={el => submenuRefs.current[item.id] = el}
                  style={{
                    position: 'relative',
                    zIndex: isMenuOpen(item.id) ? 402 : 'auto'
                  }}
                >
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ 
                      height: 'auto',
                      opacity: 1,
                      zIndex: 402
                    }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.2 }}
                    className="mt-2 pl-4 space-y-1 relative bg-background"
                  >
                    {renderSubmenu(item.submenu, level + 1)}
                  </motion.div>
                </div>
              )}
            </AnimatePresence>
          </div>
        );
      }

      return (
        <Link
          key={item.path}
          to={item.path}
          className={`flex items-center gap-3 px-4 py-2.5 rounded-lg text-foreground/70 hover:text-primary hover:bg-secondary/50 transition-colors z-999 ${
            location.pathname === item.path ? 'bg-secondary/50 text-primary' : ''
          }`}
          onClick={() => setIsOpen(false)}
        >
          <item.icon className="w-4 h-4" />
          <span>{item.name}</span>
        </Link>
      );
    });
  };

  return (
    <motion.nav
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.6 }}
      className="fixed w-full z-[100] transition-all duration-300 px-4 py-2"
    >
      <div className={`max-w-7xl mx-auto rounded-full transition-all duration-300 ${
        isScrolled
          ? 'bg-background/80 backdrop-blur-md shadow-lg'
          : 'bg-background/50 backdrop-blur-sm'
      }`}>
        <div className="flex items-center justify-between h-12 px-4 md:px-6">
          <Link 
            to="/" 
            className="flex items-center space-x-2 group shrink-0"
          >
            <motion.div className="relative text-xl font-bold whitespace-nowrap">
              <span className="bg-clip-text text-transparent bg-gradient-to-r from-primary to-primary/60">
                SIN
              </span>
              <motion.div
                className="inline-block relative"
                animate={{ 
                  scale: [1, 1.1, 1],
                  rotate: [0, 5, -5, 0]
                }}
                transition={{
                  duration: 2,
                  repeat: Infinity,
                  repeatType: "reverse"
                }}
              >
                <Mic className="w-6 h-6 inline text-primary animate-pulse" />
              </motion.div>
              <span className="bg-clip-text text-transparent bg-gradient-to-r from-primary to-primary/60">
                PSE
              </span>
            </motion.div>
          </Link>

          <div className="hidden md:flex items-center space-x-6">
            {navItems.map((item) => (
              item.submenu ? (
                <div
                  key={item.id}
                  className="relative"
                  onMouseEnter={() => setHoveredMenu(item.id)}
                  onMouseLeave={() => setHoveredMenu(null)}
                >
                  <button
                    onClick={(e) => {
                      e.preventDefault();
                      toggleMenu(item.id, e);
                    }}
                    className={`flex items-center space-x-1 transition-all relative group ${
                      selectedMenu === item.id ? 'text-primary' : 'text-foreground/80 hover:text-primary'
                    }`}
                  >
                    <item.icon className="w-4 h-4" />
                    <span>{item.label}</span>
                    <ChevronDown className={`w-4 h-4 transition-transform ${
                      selectedMenu === item.id || isMenuOpen(item.id) ? 'rotate-180' : ''
                    }`} />
                  </button>

                  <AnimatePresence>
                    {(isMenuOpen(item.id) || hoveredMenu === item.id) && (
                      <motion.div
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: 10 }}
                        transition={{ duration: 0.2 }}
                        className="absolute top-full left-0 mt-2 w-64 bg-card/95 backdrop-blur-sm rounded-lg shadow-lg border border-border/10 overflow-hidden z-[101]"
                      >
                        <div className="relative">
                          {renderSubmenu(item.submenu)}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              ) : (
                <Link
                  key={item.id}
                  to={item.path}
                  className={`flex items-center space-x-1 text-foreground/80 hover:text-primary transition-all relative group ${
                    location.pathname === item.path ? 'text-primary' : ''
                  }`}
                >
                  <item.icon className="w-4 h-4" />
                  <span>{item.name}</span>
                  <motion.div
                    className={`absolute -bottom-1 left-0 h-0.5 bg-primary transition-all ${
                      location.pathname === item.path ? 'w-full' : 'group-hover:w-full'
                    }`}
                    layoutId="underline"
                  />
                </Link>
              )
            ))}
          </div>

          <div className="flex items-center gap-4">
            <div className="relative z-[99]">
              <AuthStatus />
            </div>
            <div className="md:hidden">
              <button
                onClick={() => setIsOpen(!isOpen)}
                className="text-foreground hover:text-primary transition-colors p-2 -mr-2"
              >
                {isOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
              </button>
            </div>
          </div>
        </div>
      </div>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.2 }}
            className="md:hidden mt-2 bg-background/95 backdrop-blur-lg rounded-2xl border border-border/10 shadow-lg overflow-hidden z-[101]"
          >
            <div className="p-2 max-h-[calc(100vh-6rem)] overflow-y-auto">
              {navItems.map((item) => (
                <div key={item.id} className="mb-4">
                  {renderSubmenu([item])}
                </div>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.nav>
  );
};

export default Navbar;